<template>
  <div>
    <div class="itinerary section">
      <div class="itinerary-header">
        <h3 class="itinerary-title">
          {{ raqmodel.tour.tourName }}
        </h3>
      </div>
      <div class="itinerary-body">
        <!-- MAP SECTION -->
        <div
          v-if="itinerarymodel.mapImageUrl"
          class="map itinerary-map"
        >
          <div
            id="app-mapModal"
            data-type="general interaction"
            data-name="map"
            class="map-holder"
          >
            <map-modal
              :map-type-image="true"
              :mapurl="itinerarymodel?.mapImageUrl"
              :tourtype="raqmodel.tour"
            />
            <div class="map-image-wrap">
              <map-image-zoom
                :display-close-modal-button="false"
                :display-fullscreen-button="true"
                :image-src="itinerarymodel.mapImageUrl"
                :image-default-class="imageDefaultClass"
                :image-additional-class="imageAdditionalClass"
                :image-alt="itinerarymodel.mapImageAltTag"
              />
            </div>
          </div>
        </div>

        <!-- ITINERARY SECTION -->
        <div
          id="itinerary-section"
          class="lead itinerary-days"
          data-type="general interaction"
          data-name="itinerary"
        >
          <section class="accordions">
            <div class="accordion-tabs-wrap">
              <h3 class="itinerary-text">
                {{ dictionarymodel.itinerary }}
              </h3>

              <!-- TABS -->
              <div
                v-if="directionsmodel && directionsmodel !== 'undefined'"
                class="itinerary-switcher"
              >
                <ul class="tabs">
                  <li class="tab">
                    <a
                      class="button is-subtle is-level"
                      :class="!reverseAccordionActive ? 'active' : ''"
                      href="#direction1"
                      @click.prevent="changeDirection(false)"
                    >{{ directionsmodel?.directions[0].name }}</a>
                  </li>
                  <li
                    v-if="directionsmodel?.directions?.length > 1"
                    class="tab"
                  >
                    <a
                      class="button is-subtle is-level"
                      :class="reverseAccordionActive ? 'active' : ''"
                      href="#direction2"
                      @click.prevent="changeDirection(true)"
                    >{{ directionsmodel?.directions[1].name }}</a>
                  </li>
                </ul>
              </div>

              <!-- COLLAPSE / EXPAND -->
              <div class="accordion-tabs-container">
                <span
                  class="accordion-tab accordion-collapse"
                  aria-label="toggle"
                  tabindex="0"
                >
                  {{ dictionarymodel.collapseAll }}
                </span>
                <span
                  class="accordion-tab accordion-expand"
                  tabindex="0"
                >
                  {{ dictionarymodel.expandAll }}
                </span>
              </div>
            </div>

            <!-- COMBINED ACCORDIONS -->
            <div class="accordion-wrap">
              <itinerary-accordion
                v-for="(item, index) in accordionItems"
                :key="`accordion-${index}`"
                :day="item.day"
                :index="index"
                :uniworld="true"
                :dictionarymodel="dictionarymodel"
                :tourapi="raqmodel.tour"
              />
            </div>

            <!-- NOTES -->
            <div
              v-if="directionsmodel"
              class="accordion-body"
            >
              <div class="accordion-content">
                <div class="column">
                  <p
                    v-html="
                      !reverseAccordionActive
                        ? directionsmodel?.directions[0].dayToDayNote
                        : directionsmodel?.directions[1].dayToDayNote
                    "
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- MODAL -->
      <div
        id="itinerary-modal"
        class="itinerary-modal modal wide-modal itinerary-modal"
      >
        <div class="modal-background" />
        <div class="modal-card">
          <div class="modal-form">
            <div class="modal-card-head">
              <p class="modal-card-title is-size-6">
                Title
              </p>
              <div class="modal-card-close">
                <i class="fa fa-times fa-lg" />
              </div>
            </div>
            <form>
              <section class="modal-card-body">
                <div class="modal-card-content" />
              </section>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- HIGHLIGHTS -->
    <day-highlitght-slider
      v-if="raqmodel.tour.isCmsTour"
      :itinerarymodel="itinerarymodel"
    />
    <highlitghts-section
      :dictionarymodel="dictionarymodel"
      :highlitghts="itinerarymodel.tripHighlights"
      :raqmodel="raqmodel"
    />
  </div>
</template>

<script>
import ItineraryAccordion from './itinerary-accordion.vue';
import MapModal from '../../vue-components/map/map-modal.vue';
import MapImageZoom from '../../vue-components/map/map-image-zoom.vue';
import HighlitghtsSection from '../tour-page/highlitghtsSection.vue';
import DayHighlightsSlider from '../tour-page/dayHighlightsSlider.vue';
import EventBus from '../../vue-functions/event-bus';

export default {
  components: {
    'itinerary-accordion': ItineraryAccordion,
    'map-modal': MapModal,
    'map-image-zoom': MapImageZoom,
    'highlitghts-section': HighlitghtsSection,
    'day-highlitght-slider': DayHighlightsSlider
  },
  props: ['directionsmodel', 'itinerarymodel', 'dictionarymodel', 'raqmodel'],
  data() {
    return {
      reverseAccordionActive: false,
      imageDefaultClass: 'itinerary-map-image fullimage',
      selectedDirectionName: ''
    };
  },
  computed: {
    imageAdditionalClass() {
      return this.raqmodel.tour.isV4Tour ? 'v4map' : 'c1Map';
    },
    currentDirection() {
      if (!this.directionsmodel?.directions?.length) return null;

      const index =
        this.reverseAccordionActive && this.directionsmodel.directions.length > 1 ? 1 : 0;
      return this.directionsmodel.directions[index];
    },
    accordionItems() {
      if (this.directionsmodel?.directions?.length) {
        const direction = this.currentDirection;
        const items = [];

        // add pre-extensions
        (direction?.preExtensions || []).forEach((ext) => {
          items.push({
            day: {
              startDay: 'Optional Pre-Cruise Extension',
              title: ext.extensionTitle,
              text: [ext.extensionOverview],
              excursions: []
            }
          });
        });

        // add regular itinerary days
        (direction?.itineraries || []).forEach((day) => {
          items.push({ day });
        });

        // add post-extensions
        (direction?.postExtensions || []).forEach((ext) => {
          items.push({
            day: {
              startDay: 'Optional Post-Cruise Extension',
              title: ext.extensionTitle,
              text: [ext.extensionOverview],
              excursions: []
            }
          });
        });

        return items;
      }

      // fallback to itinerarymodel when no directions
      return (this.itinerarymodel.itinerary || []).map((day) => ({ day }));
    }
  },
  mounted() {
    if (this.directionsmodel?.directions?.length) {
      const directionIndex =
        this.reverseAccordionActive && this.directionsmodel.directions.length > 1 ? 1 : 0;
      const initialDirectionName = this.directionsmodel.directions[directionIndex].name;
      this.selectedDirectionName = initialDirectionName;
      EventBus.$emit('DIRECTION_CHANGED', initialDirectionName, directionIndex + 1);
    }
  },
  methods: {
    changeDirection(isReverse) {
      this.reverseAccordionActive = isReverse;
      const directionIndex = isReverse && this.directionsmodel?.directions?.length > 1 ? 1 : 0;
      const directionName = this.directionsmodel?.directions[directionIndex].name;
      this.selectedDirectionName = directionName;
      EventBus.$emit('DIRECTION_CHANGED', directionName, directionIndex + 1);
    }
  }
};
</script>
